'use client';

import { cn } from '@woonstad/helpers-utils';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';

import { BasicClose } from '../../icons/basic';
import { TextButton } from '../buttons/TextButton';
import { FullScreenDialog, FullScreenDialogClose, FullScreenDialogContent, FullScreenDialogTrigger } from '../dialog/FullScreenDialog';
import { Typography } from '../typography/Typography';
import { Carousel, CarouselContent, CarouselIndicator, CarouselItem, CarouselNavigationButtons, useCarousel } from './CarouselBase';

type CarouselImageProps = {
  id: string;
  Image: React.FunctionComponent<
    Pick<React.HTMLAttributes<HTMLImageElement>, 'className'> & { priority?: boolean }
  >;
  caption?: string;
  isSingle?: boolean;
};

type Props = {
  images: CarouselImageProps[];
  fullScreenImages?: CarouselImageProps[];
  testid?: string;
}

const CarouselImage = ({
  caption,
  Image,
  index,
  isSingle = false,
}: CarouselImageProps & { index?: number }) => {
  const {
    isFullScreen,
    selectedIndex,
  } = useCarousel();

  const isPriority = index === 0;

  return (
    <figure
      className={clsx(
        'text-left',
        isFullScreen && 'h-[calc(100dvh-10rem)] md:h-[calc(100dvh-10rem)] [@media(max-height:30rem)]:h-[calc(100dvh-1rem)]',
      )}
    >
      <Image
        priority={isPriority}
        className={clsx(
          'size-full object-contain',
          isSingle && 'mx-auto w-full md:max-h-[35rem]',
          !isSingle && 'bg-image',
          !isFullScreen && !isSingle && 'aspect-4/3',
          isFullScreen && 'transition-opacity duration-500',
          (isFullScreen && index !== selectedIndex) && 'opacity-50',
        )}
      />

      {caption && (
        <div className="mt-4 px-4 [@media(max-height:30rem)]:hidden md:px-0">
          <Typography
            variant="label-small"
            type="figcaption"
            textColorClassname="text-image-caption"
          >
            {caption}
          </Typography>
        </div>
      )}
    </figure>
  );
};

const GALLERY_HISTORY_STATE = 'gallery-open';

const FullScreenCarousel = ({ images }: { images: CarouselImageProps[] }) => {
  const {
    isSingle,
    selectedIndex,
    prevSlideLabel,
    nextSlideLabel,
    indicatorSeparatorLabel,
    closeOverlayLabel,
  } = useCarousel();

  const [open, setOpen] = useState(false);
  const closedByPopstate = useRef(false);

  const closeDialog = useCallback(() => {
    if (!closedByPopstate.current) {
      window.history.back();
    }
    closedByPopstate.current = false;
    setOpen(false);
  }, []);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (nextOpen) {
      window.history.pushState({ gallery: GALLERY_HISTORY_STATE }, '');
      setOpen(true);
    } else {
      closeDialog();
    }
  }, [closeDialog]);

  useEffect(() => {
    if (!open) return;

    const onPopState = () => {
      closedByPopstate.current = true;
      setOpen(false);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [open]);

  if (isSingle) return null;

  const currentSlide = images[selectedIndex];
  const isVideoSlide = currentSlide?.id === 'video-overview';

  return (
    <FullScreenDialog open={open} onOpenChange={handleOpenChange}>
      <FullScreenDialogTrigger
        aria-label="Open image carousel"
        className={cn(
          'absolute left-0 top-0 aspect-4/3 w-full overflow-hidden',
          isVideoSlide && 'pointer-events-none',
        )}
      />

      <FullScreenDialogContent className="left-0 top-0 h-full w-svw [@media(max-height:30rem)]:gap-0 [@media(max-height:30rem)]:p-2">
        <FullScreenDialogClose className="ml-auto h-10 w-fit [@media(max-height:30rem)]:absolute [@media(max-height:30rem)]:right-2 [@media(max-height:30rem)]:top-2 [@media(max-height:30rem)]:z-10 [@media(max-height:30rem)]:h-8">
          <TextButton
            iconRight={BasicClose}
          >
            <div>
              {closeOverlayLabel}
            </div>
          </TextButton>
        </FullScreenDialogClose>
        <div className="flex h-[calc(100dvh-10rem)] [@media(max-height:30rem)]:h-[calc(100dvh-1rem)] w-full items-center justify-center">
          <Carousel
            className="w-[100svw] md:w-[70vw]"
            defaultSelectedIndex={selectedIndex}
            isFullScreen
            prevSlideLabel={prevSlideLabel}
            nextSlideLabel={nextSlideLabel}
            indicatorSeparatorLabel={indicatorSeparatorLabel}
            closeOverlayLabel={closeOverlayLabel}
          >
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={image.id}>
                  <CarouselImage
                    index={index}
                    {...image}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>

            <CarouselNavigationButtons />
            <CarouselIndicator />
          </Carousel>
        </div>
      </FullScreenDialogContent>
    </FullScreenDialog>
  );
};

export const ImageCarousel = ({
  images,
  fullScreenImages,
  nextSlideLabel,
  prevSlideLabel,
  indicatorSeparatorLabel,
  closeOverlayLabel,
  testid,
}: Props & {
  nextSlideLabel: string
  prevSlideLabel: string
  indicatorSeparatorLabel: string
  closeOverlayLabel: string
}) => {
  const isSingle = images.length <= 1;
  const galleryImages = fullScreenImages ?? images;

  return (
    <Carousel
      nextSlideLabel={nextSlideLabel}
      prevSlideLabel={prevSlideLabel}
      indicatorSeparatorLabel={indicatorSeparatorLabel}
      closeOverlayLabel={closeOverlayLabel}
      testid={testid}
    >
      <CarouselContent>
        {images.map((image, index) => (
          <CarouselItem key={image.id}>
            <CarouselImage
              index={index}
              {...image}
              isSingle={isSingle}
            />
          </CarouselItem>
        ))}
      </CarouselContent>

      <FullScreenCarousel images={galleryImages} />
      <CarouselNavigationButtons />

      <CarouselIndicator />
    </Carousel>
  );
};
